import { FilePageDoc } from 'common';
import { FileService, PctRect } from '../service/FileService';

export interface SvgTextRun {
  element?: Element;
  text: string;
}

export interface PageWord {
  word: string;
  runIndex: number;
  charStart: number;
  charEnd: number;
}

export interface AlignmentResult {
  score: number;
  coverage: number;
  /** Inclusive index into pageWords of first matched page word. */
  pageStart: number;
  /** Inclusive index into pageWords of last matched page word. */
  pageEnd: number;
  /** Page-word indices that aligned to a citation word (not gaps). */
  matchedPageWordIndices: number[];
}

export interface RunMatchRange {
  runIndex: number;
  charStart: number;
  charEnd: number;
  /** True when matched span covers most of the run's alphanumeric content. */
  coverWholeRun: boolean;
}

const MATCH_SCORE = 2;
const MISMATCH_SCORE = -1;
const GAP_SCORE = -1;
const MIN_COVERAGE = 0.7;
const WHOLE_RUN_COVERAGE = 0.5;

type TraceDir = 'diag' | 'up' | 'left' | 'stop';

/**
 * SVG text geometry helpers. Citation matching uses word-level Smith–Waterman
 * so quotes that span multiple &lt;tspan&gt;/&lt;text&gt; runs still resolve.
 */
export class SVGUtils {
  static readonly MIN_COVERAGE = MIN_COVERAGE;

  static tokenizeWords(text: string): string[] {
    if (!text) return [];
    const words: string[] = [];
    const re = /[a-z0-9]+/gi;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      words.push(match[0].toLowerCase());
    }
    return words;
  }

  static editDistance(a: string, b: string): number {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const prev = new Array(b.length + 1);
    const curr = new Array(b.length + 1);
    for (let j = 0; j <= b.length; j++) prev[j] = j;
    for (let i = 1; i <= a.length; i++) {
      curr[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      }
      for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
    }
    return prev[b.length];
  }

  static wordsSimilar(a: string, b: string): boolean {
    if (a === b) return true;
    if (a.length >= 5 && b.length >= 5 && SVGUtils.editDistance(a, b) <= 1) return true;
    return false;
  }

  static buildPageWordStream(runs: Array<{ text: string }>): PageWord[] {
    const words: PageWord[] = [];
    runs.forEach((run, runIndex) => {
      const re = /[a-z0-9]+/gi;
      let match: RegExpExecArray | null;
      while ((match = re.exec(run.text)) !== null) {
        words.push({
          word: match[0].toLowerCase(),
          runIndex,
          charStart: match.index,
          charEnd: match.index + match[0].length,
        });
      }
    });
    return words;
  }

  /**
   * Word-level Smith–Waterman: best local alignment of citation words in the page stream.
   */
  static findBestAlignment(citationWords: string[], pageWords: PageWord[]): AlignmentResult | null {
    const n = citationWords.length;
    const m = pageWords.length;
    if (!n || !m) return null;

    const H: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    const ptr: TraceDir[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill('stop' as TraceDir));

    let bestScore = 0;
    let bestI = 0;
    let bestJ = 0;

    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        const similar = SVGUtils.wordsSimilar(citationWords[i - 1], pageWords[j - 1].word);
        const diag = H[i - 1][j - 1] + (similar ? MATCH_SCORE : MISMATCH_SCORE);
        const up = H[i - 1][j] + GAP_SCORE;
        const left = H[i][j - 1] + GAP_SCORE;
        const score = Math.max(0, diag, up, left);
        H[i][j] = score;
        if (score === 0) {
          ptr[i][j] = 'stop';
        } else if (score === diag) {
          ptr[i][j] = 'diag';
        } else if (score === up) {
          ptr[i][j] = 'up';
        } else {
          ptr[i][j] = 'left';
        }
        if (score > bestScore) {
          bestScore = score;
          bestI = i;
          bestJ = j;
        }
      }
    }

    if (bestScore <= 0) return null;

    const matchedPageWordIndices: number[] = [];
    let matchedCitationWords = 0;
    let i = bestI;
    let j = bestJ;
    let pageStart = bestJ - 1;
    let pageEnd = bestJ - 1;

    while (i > 0 && j > 0 && H[i][j] > 0) {
      const dir = ptr[i][j];
      if (dir === 'stop') break;
      if (dir === 'diag') {
        if (SVGUtils.wordsSimilar(citationWords[i - 1], pageWords[j - 1].word)) {
          matchedCitationWords++;
          matchedPageWordIndices.push(j - 1);
        }
        pageStart = j - 1;
        pageEnd = Math.max(pageEnd, j - 1);
        i--;
        j--;
      } else if (dir === 'up') {
        i--;
      } else {
        // left = gap in citation (extra page word)
        pageStart = j - 1;
        j--;
      }
    }

    matchedPageWordIndices.reverse();
    if (!matchedPageWordIndices.length) return null;

    pageStart = matchedPageWordIndices[0];
    pageEnd = matchedPageWordIndices[matchedPageWordIndices.length - 1];
    const coverage = matchedCitationWords / n;
    if (coverage < MIN_COVERAGE) return null;

    return {
      score: bestScore,
      coverage,
      pageStart,
      pageEnd,
      matchedPageWordIndices,
    };
  }

  static mapAlignmentToRunRanges(pageWords: PageWord[], alignment: AlignmentResult): RunMatchRange[] {
    const byRun = new Map<number, { charStart: number; charEnd: number; matchedLen: number }>();
    for (const idx of alignment.matchedPageWordIndices) {
      const pw = pageWords[idx];
      const existing = byRun.get(pw.runIndex);
      const wordLen = pw.charEnd - pw.charStart;
      if (!existing) {
        byRun.set(pw.runIndex, { charStart: pw.charStart, charEnd: pw.charEnd, matchedLen: wordLen });
      } else {
        existing.charStart = Math.min(existing.charStart, pw.charStart);
        existing.charEnd = Math.max(existing.charEnd, pw.charEnd);
        existing.matchedLen += wordLen;
      }
    }

    const runAlphaLen = new Map<number, number>();
    for (const pw of pageWords) {
      runAlphaLen.set(pw.runIndex, (runAlphaLen.get(pw.runIndex) || 0) + (pw.charEnd - pw.charStart));
    }

    return Array.from(byRun.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([runIndex, range]) => {
        const total = runAlphaLen.get(runIndex) || 1;
        return {
          runIndex,
          charStart: range.charStart,
          charEnd: range.charEnd,
          coverWholeRun: range.matchedLen / total >= WHOLE_RUN_COVERAGE,
        };
      });
  }

  /**
   * Match a quote after removing whitespace and punctuation, while retaining the
   * original run/character positions needed to measure the matching SVG text.
   * PDF-to-SVG output often concatenates words and splits lines at different
   * points than browser selection text, making word alignment impossible.
   */
  static mapNormalizedQuoteToRunRanges(quote: string, runs: SvgTextRun[]): RunMatchRange[] {
    const normalizedQuote = quote.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!normalizedQuote) return [];

    const pageChars: Array<{ char: string; runIndex: number; charIndex: number }> = [];
    runs.forEach((run, runIndex) => {
      Array.from(run.text).forEach((char, charIndex) => {
        if (/[a-z0-9]/i.test(char)) {
          pageChars.push({ char: char.toLowerCase(), runIndex, charIndex });
        }
      });
    });

    const matchStart = pageChars.map(item => item.char).join('').indexOf(normalizedQuote);
    if (matchStart < 0) return [];

    const matchedChars = pageChars.slice(matchStart, matchStart + normalizedQuote.length);
    const byRun = new Map<number, { charStart: number; charEnd: number; matchedLen: number }>();
    matchedChars.forEach(({ runIndex, charIndex }) => {
      const existing = byRun.get(runIndex);
      if (!existing) {
        byRun.set(runIndex, { charStart: charIndex, charEnd: charIndex + 1, matchedLen: 1 });
      } else {
        existing.charStart = Math.min(existing.charStart, charIndex);
        existing.charEnd = Math.max(existing.charEnd, charIndex + 1);
        existing.matchedLen++;
      }
    });

    return Array.from(byRun.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([runIndex, range]) => {
        const runAlphaLen = runs[runIndex].text.replace(/[^a-z0-9]/gi, '').length || 1;
        return {
          runIndex,
          charStart: range.charStart,
          charEnd: range.charEnd,
          coverWholeRun: range.matchedLen / runAlphaLen >= WHOLE_RUN_COVERAGE,
        };
      });
  }

  static extractTextRuns(svgRoot: SVGSVGElement): SvgTextRun[] {
    const runs: SvgTextRun[] = [];
    const textElements = svgRoot.querySelectorAll('text');
    textElements.forEach(textEl => {
      const tspans = textEl.querySelectorAll(':scope > tspan');
      if (tspans.length) {
        tspans.forEach(tspan => {
          const text = tspan.textContent || '';
          if (text.trim()) runs.push({ element: tspan, text });
        });
      } else {
        const text = textEl.textContent || '';
        if (text.trim()) runs.push({ element: textEl, text });
      }
    });
    return runs;
  }

  /**
   * Prefix all element ids (and url(#id) / href="#id" refs) so multiple inline page SVGs
   * don't clobber each other's clipPath/mask/gradient definitions.
   */
  static namespaceSvgIds(svgString: string, prefix: string): string {
    if (!svgString || !prefix) return svgString;

    const ids = new Set<string>();
    const idAttrRe = /\sid="([^"]+)"/g;
    let match: RegExpExecArray | null;
    while ((match = idAttrRe.exec(svgString)) !== null) {
      ids.add(match[1]);
    }
    if (ids.size === 0) return svgString;

    let result = svgString;
    for (const id of ids) {
      const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(`id="${escaped}"`, 'g'), `id="${prefix}${id}"`);
      result = result.replace(new RegExp(`url\\(#${escaped}\\)`, 'g'), `url(#${prefix}${id})`);
      result = result.replace(new RegExp(`href="#${escaped}"`, 'g'), `href="#${prefix}${id}"`);
      result = result.replace(new RegExp(`xlink:href="#${escaped}"`, 'g'), `xlink:href="#${prefix}${id}"`);
    }
    return result;
  }

  /**
   * Find the best fuzzy match of `quote` in the page SVG and return percent-space rects
   * (one per matched text run / highlight fragment).
   */
  static async getPositionRects(projectId: string, document: Document, quote: string, page: FilePageDoc): Promise<PctRect[]> {
    const svgString = await FileService.loadSvgToString(projectId, page);
    const svgDoc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    const documentElement = svgDoc.documentElement as unknown as SVGSVGElement;
    if (!documentElement || documentElement.querySelector('parsererror')) return [];

    const temp = document.createElement('div');
    temp.style.visibility = 'hidden';
    temp.style.position = 'absolute';
    temp.style.left = '-99999px';
    temp.style.top = '0';
    document.body.appendChild(temp);
    temp.appendChild(documentElement);

    try {
      return SVGUtils.getPositionRectsFromSvg(quote, documentElement);
    } finally {
      document.body.removeChild(temp);
    }
  }

  static getPositionRectsFromSvg(quote: string, documentElement: SVGSVGElement, restoreAfterMeasurement = false): PctRect[] {
    const runs = SVGUtils.extractTextRuns(documentElement);
    if (!runs.length) return [];

    // Prefer an exact character-stream match. This survives PDF SVGs that omit
    // spaces and wrap lines at different places than the selected citation.
    let ranges = SVGUtils.mapNormalizedQuoteToRunRanges(quote, runs);
    if (!ranges.length) {
      const citationWords = SVGUtils.tokenizeWords(quote);
      if (!citationWords.length) return [];
      const pageWords = SVGUtils.buildPageWordStream(runs);
      const alignment = SVGUtils.findBestAlignment(citationWords, pageWords);
      if (!alignment) return [];
      ranges = SVGUtils.mapAlignmentToRunRanges(pageWords, alignment);
    }
    if (!ranges.length) return [];

    const originals = new Map<Element, { className: string | null; innerHTML: string }>();
    try {
      // Mark matched runs for measurement (whole-run class or wrapped hilight-text).
      for (const range of ranges) {
        const run = runs[range.runIndex];
        const el = run.element;
        if (!el) continue;
        if (restoreAfterMeasurement && !originals.has(el)) {
          originals.set(el, { className: el.getAttribute('class'), innerHTML: el.innerHTML });
        }
        if (range.coverWholeRun) {
          el.setAttribute('class', `${el.getAttribute('class') || ''} hilight-text`.trim());
        } else {
          const raw = run.text;
          const before = raw.slice(0, range.charStart);
          const middle = raw.slice(range.charStart, range.charEnd);
          const after = raw.slice(range.charEnd);
          el.innerHTML = `${escapeXml(before)}<tspan class="hilight-text">${escapeXml(middle)}</tspan>${escapeXml(after)}`;
        }
      }

      const hilighted = documentElement.getElementsByClassName('hilight-text');
      const docBounds = documentElement.getBoundingClientRect();
      if (!docBounds.width || !docBounds.height) return [];

      return Array.from(hilighted).map(element => SVGUtils.boundsToPctRect(element, docBounds));
    } finally {
      originals.forEach((original, element) => {
        element.innerHTML = original.innerHTML;
        if (original.className === null) {
          element.removeAttribute('class');
        } else {
          element.setAttribute('class', original.className);
        }
      });
    }
  }

  /** Convert element screen bounds into 0–1 coords relative to the SVG root. */
  static boundsToPctRect(element: Element, docBounds: DOMRect): PctRect {
    const bounds = element.getBoundingClientRect();
    return {
      text: element.textContent || '',
      t: (bounds.top - docBounds.top) / docBounds.height,
      r: (bounds.right - docBounds.left) / docBounds.width,
      b: (bounds.bottom - docBounds.top) / docBounds.height,
      l: (bounds.left - docBounds.left) / docBounds.width,
    };
  }
}

function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
